import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { defaultAxios } from '..';

const contactService = {
  async fetchCustomerList(query?: string): Promise<PaginatedData<CustomerT>[]> {
    let retry = 0;
    const request = async (): Promise<PaginatedData<CustomerT>[]> => {
      try {
        const wa = getCurrWorkspaceAlias();
        const res = await defaultAxios.get(
          `/workspaces/${wa}/customers${query || ''}`,
        );
        return res.data.customers;
      } catch (e) {
        if (retry < 10) {
          retry += 1;
          return await request();
        } else {
          throw e;
        }
      }
    };
    return await request();
  },
  async fetchCustomerCount(query?: string): Promise<number> {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.get(
        `/workspaces/${wa}/customers/count${query || ''}`,
      );
      return res.data.count;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async fetchCustomer(customerAlias: string): Promise<CustomerT> {
    try {
      const res = await defaultAxios.get(`/customers/${customerAlias}`);
      return res.data.customer;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async postCustomer(customer: PostCustomerParams): Promise<CustomerT> {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.post(
        `/workspaces/${wa}/customer`,
        customer,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async uploadCustomers(customer: csvCustomerParams[]) {
    try {
      const wa = getCurrWorkspaceAlias();
      await defaultAxios.post(`/workspaces/${wa}/customer/upload`, customer);
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async deleteCustomer(alias: string) {
    try {
      defaultAxios.delete(`/customers/${alias}`);
    } catch (e) {
      return false;
    }
  },
  async resetCompany(customerAlias: string) {
    try {
      const res = await defaultAxios.put(
        `/customers/${customerAlias}/company/reset`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  updateCustomerAttr: async <K extends keyof UpdatableCustomerAttrs>(
    alias: string,
    target: K,
    value: UpdatableCustomerAttrs[K],
  ) => {
    try {
      if (typeof value === 'undefined') {
        const res = await defaultAxios.patch(
          `/customers/${alias}/${target}/reset`,
        );
        return res.data;
      }
      const res = await defaultAxios.patch(`/customers/${alias}/${target}`, {
        [target]: value,
      });
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async fetchLinkedFeedback(
    customerAlias: string,
  ): Promise<LinkedFeedbackResponse> {
    const res = await defaultAxios.get(`/customers/${customerAlias}/feedbacks`);
    return res.data;
  },
  async fetchContactName(contactAliases: string[]): Promise<AliasNamePair[]> {
    const res = await defaultAxios.post(`/customers/name`, {
      aliases: contactAliases,
    });
    return res.data;
  },
};

export default contactService;
